import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import {
  Button, Card, Form,
  Input, InputNumber, Select, Switch,
  TimePicker
} from 'antd';
import TextArea from 'antd/lib/input/TextArea';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  createTreatment, getAllConsents, getAppliances,
  getAreas, getCategories, getTreatmentById, getTreatments, getTreatmentTypes, updateTreatment
} from '../../../../appRedux/actions';
import DefaultImage from '../../../../assets/images/ICONO-AZUL-100x100.png';
import { timeFormat } from '../../../../constants';
import { appliances as applianceType } from '../../../../constants/Appliance.const';
import { treatmentConst } from '../../../../constants/Treatments.cons';
import {
  buttonLabel,
  cardConfig,
  formConfig,
  label,
  messages,
  placeHolders
} from '../../../../constants/Forms';
import { slugConst } from '../../../../constants/Slug.const';
import { setTimeFormat, validatePhotos } from '../../../../util';
import {
  CancelButton, CustomSelect, NumericInput, UploadPhotos
} from '../../../uiComponents';
import { TreatmentProducts } from '../TreatmentProducts';
import './styles.css';

export const TreatmentForm = () => {
  const { id } = useParams();
  const { treatmentById, treatments } = useSelector(
    (state) => state.treatment
  );
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { treatmentTypes } = useSelector((state) => state.treatmentType);
  const { appliances } = useSelector((state) => state.appliances);
  const { areas } = useSelector((state) => state.area);
  const { consents } = useSelector((state) => state.consent);
  const [response, setResponse] = useState();
  const [term, setTerm] = useState();
  const [assessment, setAssessment] = useState(false);
  const [consent, setConsent] = useState(false);
  const [active, setActive] = useState(true);
  const [treatmentType, setTreatmentType] = useState(treatmentConst.medical);
  const [treatmentTypeId, setTreatmentTypeId] = useState(null);
  const [treatmentAreas, setTreatmentsAreas] = useState(null);
  const [fileList, setFileList] = useState([]);
  const { categories } = useSelector((state) => state.category);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (id) {
      dispatch(getTreatmentById(id));
    }
    dispatch(getTreatmentTypes());
    dispatch(getAllConsents());
    dispatch(getAppliances({ applianceType, active: '1' }));
    dispatch(getCategories({ type: 'treatment' }));
  }, [dispatch, id]);

  useEffect(() => {
    if (response && [200, 201].includes(response)) {
      navigate('/dashboard/tratamientos');
    }
  }, [navigate, response]);

  const treatmentSelector = useMemo(
    () =>
      (Array.isArray(treatmentTypes) ? treatmentTypes : []).map(
        ({ id, name }) => ({ value: id, label: name })
      ),
    [treatmentTypes]
  );

  const relatedTreatmentSelector = useMemo(() => {
    const savedRelatedTreatments =
      treatmentById?.id === id && Array.isArray(treatmentById.relatedTreatments)
        ? treatmentById.relatedTreatments
        : [];
    const availableRelatedTreatments = (
      Array.isArray(treatments) ? treatments : []
    ).filter(
      (treatment) =>
        treatment.id !== id &&
        treatment.treatmentType?.id === treatmentTypeId
    );

    return Array.from(
      new Map(
        [...savedRelatedTreatments, ...availableRelatedTreatments].map(
          (treatment) => [treatment.id, treatment]
        )
      ).values()
    ).map(({ id: relatedTreatmentId, name, sku }) => ({
      value: relatedTreatmentId,
      label: `${sku} - ${name}`,
    }));
  }, [treatments, treatmentById, id, treatmentTypeId]);

  useEffect(() => {
    if (treatmentTypeId) {
      dispatch(getAreas({ treatmentTypeId }));
      dispatch(getTreatments({ treatmentTypeId, withDeleted: 'false' }));
    }
  }, [dispatch, treatmentTypeId, form]);

  useEffect(() => {
    if (areas) setTreatmentsAreas(areas);
  }, [areas]);

  useEffect(() => {
    if (id && treatmentById?.id === id) {
      setAssessment(treatmentById?.assessment);
      setConsent(treatmentById?.consent === 0 ? false : true);
      setActive(treatmentById?.active);
      setTerm(treatmentById?.term);
      setTreatmentTypeId(treatmentById?.treatmentType?.id);
      setTreatmentType(treatmentById?.treatmentType?.name);

      setFileList([
        {
          uid: treatmentById?.photo?.id,
          name: treatmentById?.photo?.name,
          status: 'done',
          url: treatmentById?.photo
            ? `${process.env.REACT_APP_API}/v1/photo/thumbnail/${treatmentById?.photo?.name}`
            : DefaultImage,
        },
      ]);
    }
  }, [treatmentById, id]);

  useEffect(() => {
    if (id && treatmentById?.id === id) {
      const applianceIds = treatmentById?.appliance?.map(
        (appliance) => appliance.id
      );
      const areasIds = treatmentById?.areas?.map((area) => area?.id);

      setTerm(treatmentById?.term);

      form.setFieldsValue({
        ...treatmentById,
        applianceIds,
        areasIds,
        categories: treatmentById?.categories?.map((category) => category?.id),
        treatmentTypeId: treatmentById?.treatmentType?.id,
        consentId: treatmentById?.treatmentConsent?.id,
        relatedTreatmentIds: treatmentById?.relatedTreatments?.map(
          (relatedTreatment) => relatedTreatment.id
        ),
        term: setTimeFormat(treatmentById?.term),
      });
      setListItems(treatmentById);
      form.setFieldsValue({ products: treatmentById.treatmentProduct });
    }
  }, [form, treatmentById, id]);

  const onChangePhotos = () => {};

  const setListItems = ({ treatmentProduct }) => {
    treatmentProduct?.forEach((row) => {
      row.productId = { value: row?.product?.id, label: row?.product?.name };
    });
  };

  const onSubmit = async (values) => {
    setIsLoading(true);
    const { assessment, consent, active, validity } = values;
    let products = [];

    if (values?.products?.length > 0) {
      products = values?.products?.map((product) => ({
        ...product,
        productId: product.productId.value || product.productId,
      }));
    }

    const newValues = {
      ...values,
      term: term,
      applianceIds:
        values?.applianceIds?.length > 0 ? values?.applianceIds : null,
      assessment: assessment ? 1 : 0,
      consent: consent ? 1 : 0,
      active: id ? (active ? 1 : 0) : 1,
      productsIds: products?.length > 0 ? products : null,
      validity: validity ? parseInt(validity, 10) : null,
    };

    if (id) {
      setResponse(await dispatch(updateTreatment(id, newValues)));
    } else {
      setResponse(await dispatch(createTreatment(newValues)));
    }
    setIsLoading(false);
  };

  return (
    <>
      <Card size="small" bordered={true} style={cardConfig}>
        <Form id="onSubmit" autoComplete="off" onFinish={onSubmit} form={form}>
          <Form.Item
            {...formConfig}
            label={label.photo}
            rules={[
              {
                required: id ? false : true,
                message: messages.required(label.photo),
              },
              () => ({
                validator(_, value) {
                  return validatePhotos(_, value);
                },
              }),
            ]}
            name="photos"
          >
            <UploadPhotos
              onChange={onChangePhotos}
              limit={1}
              files={fileList}
            />
          </Form.Item>

          <Form.Item
            {...formConfig}
            name="sku"
            label={label.sku}
            rules={[
              { required: true, message: messages.required(label.sku) },
              { min: 4, message: messages.validation.sku },
              { max: 10, message: messages.validation.sku },
            ]}
          >
            <Input disabled={id ? true : false} />
          </Form.Item>

          <Form.Item
            {...formConfig}
            name="name"
            label={label.name}
            rules={[
              { required: true, message: messages.required(label.name) },
              { max: 80, message: messages.lenght },
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            {...formConfig}
            name="slug"
            label={slugConst.label}
            rules={[
              {
                max: slugConst.maxLength,
                message: slugConst.maxLengthMessage,
              },
              {
                pattern: slugConst.pattern,
                message: slugConst.formatMessage,
              },
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            {...formConfig}
            name="treatmentTypeId"
            label={label.treatmentTypes}
            rules={[
              {
                required: true,
                message: messages.required(label.treatmentTypes),
              },
            ]}
          >
            <Select
              placeholder={placeHolders.treatmentType}
              allowClear
              onChange={(value, option) => {
                form.setFieldsValue({
                  areasIds: [],
                  relatedTreatmentIds: [],
                });
                setTreatmentTypeId(value);
                setTreatmentType(option?.label);
              }}
              options={treatmentSelector}
            ></Select>
          </Form.Item>

          <Form.Item
            {...formConfig}
            name="relatedTreatmentIds"
            label={label.relatedTreatments}
          >
            <CustomSelect
              mode="multiple"
              placeholder={placeHolders.relatedTreatments}
              disabled={!treatmentTypeId}
              options={relatedTreatmentSelector}
            ></CustomSelect>
          </Form.Item>

          <Form.Item
            {...formConfig}
            name="description"
            label={label.description}
            rules={[
              {
                required: true,
                message: messages.required(label.description),
              },
            ]}
          >
            <TextArea />
          </Form.Item>

          <Form.Item
            {...formConfig}
            name="categories"
            label={label.categories}
            rules={[
              {
                required: true,
                message: messages.required(label.categories),
              },
            ]}
          >
            <CustomSelect
              mode="multiple"
              placeholder={placeHolders.categories}
              allowClear
              options={categories.map(({ id, name }) => ({
                value: id,
                label: name,
              }))}
            ></CustomSelect>
          </Form.Item>

          <Form.Item
            {...formConfig}
            name="price"
            label={label.price}
            rules={[
              { required: true, message: messages.required(label.price) },
            ]}
          >
            <InputNumber
              formatter={(value) =>
                `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
              }
              parser={(value) => value?.replace(/\$\s?|(,*)/g, '')}
              stringMode={true}
            />
          </Form.Item>

          {treatmentType === treatmentConst.medical && (
            <Form.Item
              {...formConfig}
              name="medicalFee"
              label={label.commission}
              rules={[
                {
                  required: true,
                  message: messages.required(label.commission),
                },
              ]}
            >
              <NumericInput />
            </Form.Item>
          )}

          <Form.Item
            {...formConfig}
            name="points"
            label={label.points}
            rules={[
              { required: true, message: messages.required(label.points) },
            ]}
          >
            <NumericInput style={{ width: '100%' }} />
          </Form.Item>
          {treatmentType !== treatmentConst.valuation && areas && (
            <Form.Item
              {...formConfig}
              name="areasIds"
              label={label.areas}
              rules={[
                { required: true, message: messages.required(label.areas) },
              ]}
            >
              <CustomSelect
                mode="multiple"
                placeholder={placeHolders.aplication}
                allowClear
                options={treatmentAreas?.map(({ id, name }) => ({
                  value: id,
                  label: name,
                }))}
              ></CustomSelect>
            </Form.Item>
          )}

          <Form.Item
            {...formConfig}
            name="consent"
            label={label.requiresConsent}
          >
            <Switch
              checked={consent}
              checkedChildren={<CheckOutlined />}
              unCheckedChildren={<CloseOutlined />}
              onChange={setConsent}
            />
          </Form.Item>

          {consent && (
            <Form.Item
              {...formConfig}
              name="consentId"
              label={label.consents}
              rules={[
                {
                  required: true,
                  message: messages.required(label.consent),
                },
              ]}
            >
              <CustomSelect
                placeholder={placeHolders.consent}
                allowClear
                options={consents.map(({ id, name }) => ({
                  value: id,
                  label: name,
                }))}
              ></CustomSelect>
            </Form.Item>
          )}
          {treatmentType !== treatmentConst.valuation && (
            <Form.Item
              {...formConfig}
              name="assesment"
              label={label.assessment}
            >
              <Switch
                checked={assessment}
                checkedChildren={<CheckOutlined />}
                unCheckedChildren={<CloseOutlined />}
                onChange={setAssessment}
              />
            </Form.Item>
          )}
          {id && (
            <Form.Item
              {...formConfig}
              name="active"
              label={label.activeText}
            >
              <Switch
                checked={active}
                checkedChildren={<CheckOutlined />}
                unCheckedChildren={<CloseOutlined />}
                onChange={setActive}
              />
            </Form.Item>
          )}

          <Form.Item
            {...formConfig}
            name="term"
            label={label.duration}
            rules={[
              {
                required: true,
                message: messages.required(label.duration),
              },
            ]}
          >
            <TimePicker
              format={timeFormat}
              minuteStep={30}
              onChange={(_, timeString) => setTerm(timeString)}
            />
          </Form.Item>

          <Form.Item
            {...formConfig}
            name="validity"
            label={label.validity}
            rules={[
              {
                pattern: /^[1-9]\d*$/,
                message: messages.validation.greaterThanZero,
              },
            ]}
          >
            <NumericInput style={{ width: '100%' }} />
          </Form.Item>

          {treatmentType !== treatmentConst.valuation && appliances && (
            <Form.Item
              {...formConfig}
              name="applianceIds"
              label={label.appliances}
            >
              <CustomSelect
                mode="multiple"
                allowClear
                options={appliances.map(({ id, name }) => ({
                  value: id,
                  label: name,
                }))}
                placeholder={placeHolders.appliances}
              ></CustomSelect>
            </Form.Item>
          )}

          {treatmentType !== treatmentConst.valuation && (
            <TreatmentProducts form={form} />
          )}

          <Button
            type="primary"
            htmlType="submit"
            className="btn-form"
            form="onSubmit"
            loading={isLoading}
          >
            {buttonLabel.save}
          </Button>
        </Form>
        <CancelButton path="/dashboard/tratamientos" />
      </Card>
    </>
  );
};
